import { featureConfigMap as loyalty } from '@retail/loyalty-stars-config';
import {
    AccountsCurrentFeatureConfig,
    AccountsFeatureConfig,
    BuildingSocietyFeatureConfig,
    ProductsFeatureConfig,
    SavingsFeatureConfig,
    StatementFeatureConfig,
} from '@retail/products-config';
import { infoApi } from '@shared/info-api-config';
import { appointments } from '@retail/appointments-config';
import { documentsAndRequests } from '@shared/documents-and-requests-config';
import _ from '@luna/lodash';
import { contactDataManagement } from '@shared/contact-data-management-config';
import { messenger } from '@shared/messenger-config';
import { cards } from '@retail/cards-config';
import { exportFeatures, transactions } from '@retail/transactions-config';
import { thirdPartyProviders } from '@shared/third-party-providers-config';
import { featureConfigMap as store } from '@shared/store-config';
import { supportChatFeatureConfigMap } from '@shared/support-chat-config';
import { securitySettings } from '@shared/security-settings-config';
import { profileSharedConfig } from '@shared/profile-config';
import { featureConfigMap as pfm } from '@retail/pfm-config';
import type { DateFormat } from '@luna/date';
import { notificationsFeatureConfig } from '@retail/notifications-config';
import { moneyAutomationFeatureConfigMap } from '@retail/money-automation-config';

import { arrayOverwrite, baseFeatureConfig, extendFeatureConfigFromUrl } from './base';
import type { PartialRetailFeatureConfig } from './featureConfig';

export const countryFeatures: PartialRetailFeatureConfig = {
    appointments: appointments.HU,
    contactDataManagement: contactDataManagement.HU,
    documentsAndRequests: documentsAndRequests.HU,
    messenger: messenger.HU,
    cards: cards.HU,
    thirdPartyProviders: thirdPartyProviders.HU,
    statements: StatementFeatureConfig.HU,
    transactions: transactions.HU,
    export: exportFeatures.HU,
    savings: SavingsFeatureConfig.HU,
    supportChat: supportChatFeatureConfigMap.HU,
    securitySettings: securitySettings.HU,
    notifications: notificationsFeatureConfig.HU,
    base: {
        country: 'HU',
        newAccountModel: true,
        ibanCountryCode: 'HU',
        defaultLanguage: 'hu',
        allowedLanguages: ['en', 'hu'],
        availableLanguages: ['en', 'hu', 'en-HK'],
        defaultCurrency: 'HUF',
        defaultCurrencySign: 'HUF',
        loanProductAmount: 'balance',
        mainProductAmount: 'disposable',
        defaultCurrencyPre: false,
        thousandSeparator: ' ',
        saveLanguageChangesToPresto: false,
        sendLanguageHeader: true,
        usingIban: false,
        unauthorizedStatusCode: '403',
        aliasValidationStringWithoutGlobal:
            /[0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz áßäčďéěíĺľňóöôřŕšťúůüýžćĆđĐŽŠČÁÄĎÉĚÍĹĽŇÓÖÔŘŔŤÚŮÜÝ,.;:!?@&+\-*/()_#]/i.toString(),
        useVersionHeaders: false,
        latestVersionHeader: null,
        doPreCurrencyCheck: false,
        defaultTimezone: 'Europe/Budapest',
        defaultPrecision: 0,
        defaultDateFormat: 'yyyy.MM.dd' as DateFormat,
        defaultDateFormatShort: 'MMM d' as DateFormat,
        defaultDateFormatVerbose: 'yyyy MMMM d.' as DateFormat,
        defaultDateTimeFormat: 'yyyy.MM.dd HH:mm' as DateFormat,
        defaultDateTimeFullFormat: 'yyyy.MM.dd HH:mm:ss' as DateFormat,
        languageOverrides: {
            en: 'en-HK',
        },
        passCredentialsInRequest: true,
    },
    footer: {
        links: {
            imprint: {
                hu: 'https://www.erstebank.hu/hu/impresszum',
                en: 'https://www.erstebank.hu/hu/impresszum',
            },
            privacy: {
                hu: 'https://www.erstebank.hu/hu/adatkezelesi-tajekoztato',
                en: 'https://www.erstebank.hu/hu/adatkezelesi-tajekoztato',
            },
            termsAndConditions: {
                hu: 'https://gate.erstebank.hu/aszf',
                en: 'https://gate.erstebank.hu/aszf',
            },
            atm: {
                hu: 'https://www.erstebank.hu/hu/ebh-nyito/mindennapi-penzugyek/elektronikus-szolgaltatasok/atm-szolgaltatasok',
                en: 'https://www.erstebank.hu/hu/ebh-nyito/mindennapi-penzugyek/elektronikus-szolgaltatasok/atm-szolgaltatasok',
            },
        },
    },
    accounts: AccountsFeatureConfig.HU,
    handover: {
        handoverTokenExchange: true,
    },
    loyaltyStars: loyalty.HU,
    moneyAutomation: moneyAutomationFeatureConfigMap.HU,
    settings: {
        changePassword: false,
    },
    currentAccount: AccountsCurrentFeatureConfig.HU,
    accountReservations: {
        isTransactionTypeUsed: true,
        expirationDate: true,
        type: true,
    },
    buildingSociety: BuildingSocietyFeatureConfig.HU,
    store: store.HU,
    pfm: {
        ...pfm.HU,
    },
    infoApi: infoApi.HU,
    profile: profileSharedConfig.HU,
    devTools: {
        availableEnvironments: {
            Local: 'http://localhost:8080',
            Dev: 'https://hu.dev.beeone.at',
            'FAT BLUE': 'https://george.blue.fat.erstebank.hu',
            'FAT GREEN': 'https://george.green.fat.erstebank.hu',
            'PERF 1': 'https://georgeperf.erstebank.hu',
            'PERF 3': 'https://georgeperf3.erstebank.hu',
        },
    },
    tracking: {
        appId: 'INT94',
        appName: 'GeorgeWeb_Retail_HU',
        instituteId: 783,
    },
    products: ProductsFeatureConfig.HU,
    allowedRedirectDomains: ['erstebank.hu', 'erste.hu', 'ersteinvestment.hu'],
};

const featureConfigWithBase = _.merge({}, baseFeatureConfig, countryFeatures, arrayOverwrite);
export const featureConfig = extendFeatureConfigFromUrl(featureConfigWithBase);
